import type { LaneGroup, LaneStyle } from './unified-process-graph.type';

const ROW_SPACING = 100;
const STATUS_X = 120;
const EVENT_X = 150;
const SESSION_X = 420;

/** Prefix the API puts on agent session node ids (`session_<uuid>`). */
const AGENT_SESSION_NODE_PREFIX = 'session_';

/** Turns sit just right of their session's dot, so the session spine stays readable. */
const TURN_X = SESSION_X + 24;
/**
 * Content width for turn labels. With the card's padding and border (content-box
 * sizing adds ~22px) this keeps them clear of the commit handoff at `PR_X - 10`.
 */
const TURN_LABEL_MAX_WIDTH = 150;
/**
 * Content width for commit cards. The card starts 20px right of the commit dot
 * and, with ~22px of padding and border, stops short of the PR lifecycle lane
 * at `PR_X + 125`, so longer subjects are cut off with an ellipsis.
 */
const COMMIT_LABEL_MAX_WIDTH = 80;
/** Turns shown when a session is first expanded; the rest sit behind a "+N more" row. */
const TURN_PREVIEW_LIMIT = 5;

// --- Expanded-session track ---
// An expanded session lays its turns, and the commits it made, out as equal
// steps in time order along a thin track under the session dot. Each turn gets
// a marker on the track; the real time between consecutive steps is labelled
// on the track instead of being drawn to scale.
/** Centre of the track, directly under the session dot (dot is 10px wide at `SESSION_X`). */
const TRACK_CENTER_X = SESSION_X + 5;
/** Room between the session dot and the first step, clear of the "Hide turns" toggle. */
const TRACK_TOP_OFFSET = 80;
/** Vertical distance between consecutive steps (a two-line card is ~43px tall). */
const TRACK_EVENT_STEP = 70;
/** Side of the square turn marker on the track. */
const TRACK_MARKER_SIZE = 8;
/** Space kept below the last step before the next row starts. */
const TRACK_BOTTOM_PADDING = 30;
/** Gaps between steps at least this long get the "⋯ 2d 4h ⋯" long-pause style. */
const TRACK_LONG_GAP_MS = 60 * 60 * 1000;

/** Node id prefixes for turn-lane nodes; turn ids are raw UUIDs from the API. */
const TURN_NODE_PREFIX = 'turn_';
const MORE_TURNS_NODE_PREFIX = 'turns_more_';

const PR_X = 650;
const PR_LIFECYCLE_X = PR_X + 125;
const PR_HANDOFF_X = PR_X + 10;
const PR_COLUMN_WIDTH = 300;
const HANDOFF_X = SESSION_X - 15;
const COMMIT_HANDOFF_X = PR_X - 10;

/** Column position, colour and label width for each graph lane, keyed by lane name. */
const LANE_STYLES: Record<string, LaneStyle> = {
  task_event: { x: EVENT_X, bg: '#2196F3', width: 350 },
  agent_session: { x: SESSION_X, bg: '#7B1FA2', width: 280 },
  agent_turn: { x: TURN_X, bg: '#AB47BC', width: TURN_LABEL_MAX_WIDTH },
  agent_turn_more: { x: TURN_X, bg: '#AB47BC', width: TURN_LABEL_MAX_WIDTH },
  handoff: { x: HANDOFF_X, bg: '#2196F3', width: 0 },
  pr_lifecycle: { x: PR_LIFECYCLE_X, bg: '#E65100', width: 280 },
  commit: { x: PR_X, bg: '#00897B', width: 280 },
  repo_header: { x: PR_X, bg: '#455A64', width: 200 },
};

// --- Previews and labels ---
/** Characters of a PR comment kept for its hover preview. */
const COMMENT_PREVIEW_LENGTH = 160;
/** Characters of a commit message kept for its hover preview. */
const COMMIT_MESSAGE_PREVIEW_LENGTH = 600;
/** Lines of the message kept for the hover preview, so long bodies cannot outgrow the viewport. */
const COMMIT_MESSAGE_PREVIEW_LINES = 12;
/** Characters of a turn's prompt shown on its card; short enough to fit on one line. */
const TURN_PROMPT_PREVIEW_LENGTH = 24;
/** Event types counted as tool calls; matches the Agent Miner transcript's `TurnCard`. */
const TOOL_CALL_EVENT_TYPES = new Set(['TOOL_CALL', 'TOOL_ERROR']);

// --- Layout ---
/** Spacing between PR lifecycle items stacked above the first or below the last commit. */
const PR_LIFECYCLE_STACK_STEP = ROW_SPACING * 0.6;
/** Lanes laid out in the gaps between task events. */
const BRANCH_LANES = new Set(['agent_session', 'agent_turn', 'agent_turn_more', 'commit']);
/** Lanes that only exist while a session is expanded. */
const TURN_LANES = new Set(['agent_turn', 'agent_turn_more']);
/** Width of an expanded session's track rail, in px. */
const TRACK_WIDTH = 2;
/** Node id prefix for the markers on an expanded session's track. */
const TURN_MARKER_NODE_PREFIX = 'turn_marker_';
/** Gap labels sit just right of the track, between the cards' dots and labels. */
const TRACK_GAP_LABEL_OFFSET_X = 7;
/** Height of a track gap label, in px; the label is centred on its y. */
const TRACK_GAP_LABEL_HEIGHT = 14;

/** Legend groups, each hiding the lanes whose nodes fall inside its x range. */
const LANE_GROUPS: LaneGroup[] = [
  { key: 'status', label: 'Status', color: '#4CAF50', xRange: [STATUS_X, STATUS_X] },
  { key: 'task', label: 'Task Events', color: '#2196F3', xRange: [EVENT_X, EVENT_X] },
  { key: 'agent', label: 'Agent', color: '#7B1FA2', xRange: [HANDOFF_X, TURN_X] },
  {
    key: 'pr',
    label: 'Pull Requests',
    color: '#E65100',
    xRange: [PR_LIFECYCLE_X, PR_LIFECYCLE_X + 10 * PR_COLUMN_WIDTH],
  },
  {
    key: 'commit',
    label: 'Commits',
    color: '#00897B',
    xRange: [COMMIT_HANDOFF_X, PR_HANDOFF_X + 10 * PR_COLUMN_WIDTH],
  },
];

// --- Chart rendering ---
/** Diameter of a graph node's dot, in px. */
const DOT_SIZE = 10;
/** Width of the PR comment hover tooltip, in px. */
const COMMENT_TOOLTIP_WIDTH = 260;
/** Monospace font stack for commit hashes and messages and comment file paths. */
const MONOSPACE_FONT = 'ui-monospace, Menlo, monospace';
/** Max width of a node label, in px, for lanes without their own width. */
const LABEL_MAX_WIDTH = 200;
/** Invisible ReactFlow handle, for nodes that connect edges but should show no handle. */
const HIDDEN_HANDLE_STYLE = {
  background: 'transparent',
  border: 'none',
  width: 1,
  height: 1,
  minWidth: 0,
  minHeight: 0,
};

export {
  AGENT_SESSION_NODE_PREFIX,
  BRANCH_LANES,
  COMMENT_PREVIEW_LENGTH,
  COMMENT_TOOLTIP_WIDTH,
  COMMIT_HANDOFF_X,
  COMMIT_LABEL_MAX_WIDTH,
  COMMIT_MESSAGE_PREVIEW_LENGTH,
  COMMIT_MESSAGE_PREVIEW_LINES,
  DOT_SIZE,
  HANDOFF_X,
  HIDDEN_HANDLE_STYLE,
  LABEL_MAX_WIDTH,
  LANE_GROUPS,
  LANE_STYLES,
  MONOSPACE_FONT,
  MORE_TURNS_NODE_PREFIX,
  PR_COLUMN_WIDTH,
  PR_HANDOFF_X,
  PR_LIFECYCLE_STACK_STEP,
  ROW_SPACING,
  SESSION_X,
  STATUS_X,
  TOOL_CALL_EVENT_TYPES,
  TRACK_BOTTOM_PADDING,
  TRACK_CENTER_X,
  TRACK_EVENT_STEP,
  TRACK_GAP_LABEL_HEIGHT,
  TRACK_GAP_LABEL_OFFSET_X,
  TRACK_LONG_GAP_MS,
  TRACK_MARKER_SIZE,
  TRACK_TOP_OFFSET,
  TRACK_WIDTH,
  TURN_LABEL_MAX_WIDTH,
  TURN_LANES,
  TURN_MARKER_NODE_PREFIX,
  TURN_NODE_PREFIX,
  TURN_PREVIEW_LIMIT,
  TURN_PROMPT_PREVIEW_LENGTH,
};
